import { Box, MenuItem, TextField, Typography } from "@mui/material";
import useApi from "hooks/useApi";
import { useCallback, useMemo, useState } from "react";
import { HiMiniChevronUpDown } from "react-icons/hi2";
import ReactLoading from "react-loading";
import { toast } from "react-toastify";
import SmsConfirm from "./SmsConfirm";

// Loading Component
const LoadingSpinner = ({ size = "30px" }) => (
  <ReactLoading type="bubbles" color="#405189" height={size} width={size} />
);

// Dropdown State Component
const DropdownState = ({
  selectedLabel,
  isPermit,
  value,
  width,
  selectedColor,
  options,
  handleChangeStatus,
  handleStatusClose,
  isFetchStatusOptionsEndpoint,
  sx,
}) => (
  <TextField
    title={selectedLabel}
    disabled={!isPermit}
    size="small"
    value={value}
    select
    variant="standard"
    onChange={(e) => handleChangeStatus(e.target.value)}
    onClose={handleStatusClose}
    sx={{
      width,
      "& > div:before": { content: "none" },
      "& > div:after": { content: "none" },
      textAlign: "left",
      "& .MuiInputBase-input": {
        fontSize: "0.9rem",
        fontWeight: 500,
        textTransform: "capitalize",
        pt: "2px",
        color: selectedColor,
        borderRadius: "6px",
        backgroundColor: selectedLabel && `${selectedColor}10`,
        padding: "6px 10px",
        "&:hover": {
          backgroundColor: selectedLabel && `${selectedColor}20`,
        },
        "&:focus": {
          backgroundColor: selectedLabel && `${selectedColor}20`,
          borderRadius: "6px",
        },
      },
      "& svg": {
        fontSize: "16px",
        mr: "4px",
        color: selectedColor,
      },
      ...sx,
    }}
    SelectProps={{
      ...(isFetchStatusOptionsEndpoint && {
        open: true,
        onClose: handleStatusClose,
      }),
      IconComponent: HiMiniChevronUpDown,
    }}
  >
    {options?.map((status) => (
      <MenuItem value={status?.id} key={status?.id}>
        {status?.name ?? status?.label}
      </MenuItem>
    ))}
  </TextField>
);

// Non-Editable State Component
const NonEditableState = ({ selectedLabel, selectedColor, width, sx }) => (
  <Box sx={{ width }}>
    <Typography
      title={selectedLabel}
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        width: "fit-content",
        maxWidth: "130px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textTransform: "capitalize",
        textAlign: "center",
        padding: "6px 10px",
        borderRadius: "6px",
        cursor: "default",
        color: selectedColor,
        backgroundColor: selectedLabel && `${selectedColor}10`,
        ...sx,
      }}
    >
      {selectedLabel ?? "-"}
    </Typography>
  </Box>
);

// Closed State Component
const ClosedState = ({
  selectedLabel,
  selectedColor,
  width,
  label,
  isPermit,
  handleStatusOpen,
  getStatusOptionsLoading,
  sx,
}) => (
  <Box
    title={selectedLabel}
    onClick={isPermit ? handleStatusOpen : undefined}
    sx={{ position: "relative", width }}
  >
    <Typography
      sx={{
        cursor: "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "0.9rem",
        fontWeight: 500,
        textTransform: "capitalize",
        color: selectedColor,
        backgroundColor: selectedLabel && `${selectedColor}10`,
        "&:hover": {
          backgroundColor: selectedLabel && `${selectedColor}20`,
        },
        borderRadius: "6px",
        padding: "7px 30px 6px 10px",
        ...sx,
      }}
    >
      {label}
    </Typography>
    <Box
      style={{
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
      }}
    >
      {getStatusOptionsLoading ? (
        <LoadingSpinner size="30px" />
      ) : (
        <HiMiniChevronUpDown
          size={16}
          style={{
            color: selectedColor,
            position: "absolute",
            top: "50%",
            right: 4,
            transform: "translateY(-50%)",
          }}
        />
      )}
    </Box>
  </Box>
);

const StatusColumn = ({
  id,
  value,
  label,
  color,
  isPermit,
  options: initialOptions,
  setAllData,
  onSubmit,
  onSuccess,
  route,
  fetchStatusOptionsEndpoint,
  sx,
  width = "130px",
}) => {
  // State management
  const isFetchStatusOptionsEndpoint = !!fetchStatusOptionsEndpoint?.url;
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(
    !isFetchStatusOptionsEndpoint,
  );
  const [selectedStatusDialog, setSelectedStatusDialog] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);

  // API hooks
  const { fetchData: updateData, loading: updateLoading } = useApi();
  const { fetchData: fetchStatusOptions, loading: getStatusOptionsLoading } =
    useApi();

  // Memoized computed values
  const options = useMemo(
    () => (isFetchStatusOptionsEndpoint ? statusOptions : initialOptions),
    [isFetchStatusOptionsEndpoint, statusOptions, initialOptions],
  );

  const selectedStatus = useMemo(
    () => options?.find((status) => status?.id === value),
    [options, value],
  );

  const selectedColor = useMemo(
    () => color || selectedStatus?.color || "#405189",
    [color, selectedStatus],
  );

  const selectedLabel = useMemo(
    () => label || selectedStatus?.name || selectedStatus?.label,
    [label, selectedStatus],
  );

  // Event handlers
  const handleStatusUpdate = useCallback(
    async (statusId) => {
      try {
        const result = await updateData(onSubmit(statusId));
        if (result.success) {
          if (onSuccess) {
            onSuccess(result);
          } else {
            setAllData?.((prev) =>
              prev.map((data) =>
                data.id === id ? { ...data, status: result.data.status } : data,
              ),
            );
          }
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        toast.error("Something went wrong!");
      }
    },
    [updateData, onSubmit, onSuccess, setAllData, id],
  );

  const handleChangeStatus = useCallback(
    async (statusId) => {
      const targetStatus = options?.find((status) => status.id === statusId);
      if (targetStatus?.send_sms) {
        setSelectedStatusDialog(targetStatus);
      } else {
        await handleStatusUpdate(statusId);
      }
    },
    [options, handleStatusUpdate],
  );

  const handleDialogAction = useCallback(
    async (confirm) => {
      if (confirm && selectedStatusDialog) {
        await handleStatusUpdate(selectedStatusDialog?.id);
      }
      setSelectedStatusDialog(null);
    },
    [handleStatusUpdate, selectedStatusDialog],
  );

  const handleStatusOpen = useCallback(async () => {
    if (!isFetchStatusOptionsEndpoint) return;

    setStatusDropdownOpen(true);
    try {
      const result = await fetchStatusOptions(
        fetchStatusOptionsEndpoint,
        false,
      );
      if (result.success) {
        setStatusOptions(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch status options:", error);
    }
  }, [
    fetchStatusOptions,
    fetchStatusOptionsEndpoint,
    isFetchStatusOptionsEndpoint,
  ]);

  const handleStatusClose = useCallback(() => {
    setStatusDropdownOpen(false);
    setStatusOptions([]);
  }, []);

  // Render logic
  const renderContent = () => {
    if (updateLoading) {
      return (
        <Box sx={{ width }}>
          <LoadingSpinner />
        </Box>
      );
    }

    if (!isPermit) {
      return (
        <NonEditableState
          selectedLabel={selectedLabel}
          selectedColor={selectedColor}
          width={width}
          sx={sx}
        />
      );
    }

    if (statusDropdownOpen && !getStatusOptionsLoading) {
      return (
        <DropdownState
          selectedLabel={selectedLabel}
          isPermit={isPermit}
          value={value}
          width={width}
          selectedColor={selectedColor}
          options={options}
          handleChangeStatus={handleChangeStatus}
          handleStatusClose={handleStatusClose}
          isFetchStatusOptionsEndpoint={isFetchStatusOptionsEndpoint}
          sx={sx}
        />
      );
    }

    return (
      <ClosedState
        selectedLabel={selectedLabel}
        selectedColor={selectedColor}
        width={width}
        label={label}
        isPermit={isPermit}
        handleStatusOpen={handleStatusOpen}
        getStatusOptionsLoading={getStatusOptionsLoading}
        sx={sx}
      />
    );
  };

  return (
    <>
      {renderContent()}
      {selectedStatusDialog?.id && (
        <SmsConfirm
          open={true}
          setOpen={(isOpen) => !isOpen && setSelectedStatusDialog(null)}
          onAction={handleDialogAction}
          smsBody={selectedStatusDialog?.sms_body || ""}
          selectedRows={[id]}
          route={route}
        />
      )}
    </>
  );
};

export default StatusColumn;
